import React, { createContext, useContext, useState, useEffect } from 'react';
import { createZitadelAuth } from '@zitadel/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStoredUser, isAccessTokenExpired } from '../services/authToken';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authClient, setAuthClient] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [hasSlackRole, setHasSlackRole] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const client = createZitadelAuth({
      authority: process.env.REACT_APP_ZITADEL_AUTHORITY,
      client_id: process.env.REACT_APP_ZITADEL_CLIENT_ID,
      redirect_uri: window.location.origin + '/login/callback',
      post_logout_redirect_uri: window.location.origin + '/login',
      response_type: 'code',
      scope: 'openid profile email',
    });

    setAuthClient(client);

    const checkAuth = () => {
      const parsed = getStoredUser();

      // An expired token is worse than no token: every API call would come back 401 and the screens
      // would just show their generic "failed to load" errors. Treat it as signed out so the user is
      // sent back through login instead.
      if (parsed && isAccessTokenExpired()) {
        console.log('Zitadel session expired, signing out');
        setIsAuthenticated(false);
        setUser(null);
        setUserInfo(null);
        setHasSlackRole(false);
        setIsLoading(false);
        if (location.pathname !== '/login' && location.pathname !== '/login/callback') {
          navigate('/login');
        }
        return;
      }

      if (parsed) {
        try {
          const profile = parsed.profile || {};

          // Set user basic info
          setUser(profile);

          // Set extended user info with the API token and other details
          const userDetails = {
            fullName: profile.name || `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
            email: profile.email || '',
            roles: profile.roles || [],
            // access_token, not id_token: this is what the Slack API introspects. An id_token is
            // issued to this app rather than to the API, so introspection reports it inactive.
            accessToken: parsed.access_token || '',
            claims: profile,
          };

          setUserInfo(userDetails);

          // Log user information to console
          // console.log('User authenticated:', userDetails);
          // console.log('JWT Token:', parsed.id_token);
          // console.log('User roles:', profile.roles);
          // console.log('User claims:', profile);

          const rolesNew = profile["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
          // console.log('User roles new:', rolesNew);

          //check if user has role "slack"
          if (rolesNew && rolesNew.includes('BtSlackApp')) {
            setIsAuthenticated(true);
            setHasSlackRole(true);
            console.log('User has role slack');
            // If we're on the login page or callback page, redirect to home
            if (location.pathname === '/login' || location.pathname === '/login/callback') {
              navigate('/');
            }
          } else {
            console.log('User does not have role slack');
            setHasSlackRole(false);
            setIsAuthenticated(true);
            // Redirect to not-authorized, if not already there
            if (location.pathname !== '/not-authorized') {
              navigate('/not-authorized');
            }
          }

        } catch (error) {
          console.error('Error parsing user info:', error);
          setUser(null);
          setUserInfo(null);
          setIsAuthenticated(false);
          setHasSlackRole(false);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserInfo(null);
        setHasSlackRole(false);
      }
      setIsLoading(false); // Set loading to false after auth check completes
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [navigate, location]);

  const login = async () => {
    try {
      await authClient?.authorize();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const logout = async () => {
    try {
      await authClient?.signout();
      setIsAuthenticated(false);
      setUser(null);
      setUserInfo(null);
      setHasSlackRole(false);
      console.log('User logged out');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, userInfo, hasSlackRole, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
